<template>
  <div class="card">
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <div class="card-body">
          <b-skeleton width="85%"></b-skeleton>
          <b-skeleton width="55%"></b-skeleton>
          <b-skeleton width="70%"></b-skeleton>
        </div>
      </template>
      <div class="card-body">
        <div class="row mb-4">
          <div class="col-md-9">
            <div class="d-flex flex-wrap">
              <div>
                <h5 class="mb-1">{{ $t('dashboard.title.monthlyFuelEfficiency') }}</h5>
                <p class="mb-0 font-size-10">
                  {{ `${periodLabel[0]} - ${periodLabel[1]}` }}
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="d-flex align-items-center mc-table-view">
              <span class="mc-label">{{ $t('global.object.period') }} </span>
              <mc-date-picker
                class="ms-2"
                type="month"
                v-model="period"
                format="MMM YYYY"
                value-type="YYYY-MM-DD"
                :placeholder="$t('report.object.reportPeriod')"
                @pick="pickDate"
                @close="closePicker"
                :confirm="true"
                :draft="true"
                :confirm-text="$t('global.action.apply')"
                range-separator=" - "
                range
                ref="periodePicker"
                :disabled-date="disabledRange"
                :clearable="false"
                @input="applyFilter"
                :lang="datePickerLang"
              ></mc-date-picker>
            </div>
          </div>
        </div>
        <div>
          <LineChart
            :labels="chartData.labels"
            :datasets="chartData.datasets"
            yLabel="Km/L"
            ref="lineChartRef"
            :options="{
              legend: {
                position: 'bottom',
                fullWidth: false,
                labels: {
                  boxWidth: 10,
                },
              },
            }"
          >
          </LineChart>
        </div>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import LineChart from '@/components/Chart/Line.vue'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import { GET_MONTHLY_FUEL_COMPARISON } from '@/services/api/dashboard/dashboard'
import datePickerLang from '@/helpers/date-picker-lang'
import McDatePicker from '@/components/McDatePicker'
import { mapGetters } from 'vuex'

moment.locale('id')

export default {
  components: { LineChart, McDatePicker },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_TYPE,
      variables() {
        return {
          filter: {
            hasFuelSensor: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.categoryOptions = data.vehicleTypes.vehicleTypes.data.map((item) => {
            return {
              vehicleTypeId: item.vehicleTypeId,
              label: `${item.model}/${item.type}`,
            }
          })
          this.fleetCategory = this.categoryOptions
        }
      },
    },
  },
  props: {
    isLayoutEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      phaseOneShow: false,
      isLoading: false,
      isFuel: false,
      period: [moment().subtract(5, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      periodLabel: [],
      pastDate: Helper.methods.momentAdd(new Date(), -6, 'M'),
      futureEnd: moment().add(1, 'years'),
      fleetCategory: [],
      categoryOptions: [],
      chartData: {
        labels: [],
        datasets: [],
      },
      colorIndex: ['#0d4491', '#f41d00', '#ff8a00', '#137c55', '#1c74da'],
      vgroupId: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    datePickerLang() {
      return datePickerLang()
    },
  },
  async mounted() {
    this.isLoading = true
    let config = JSON.parse(localStorage.getItem('monthly-efficiency-config'))
    if (!config) {
      await this.initLocalStorage()
      config = JSON.parse(localStorage.getItem('monthly-efficiency-config'))
    }
    await this.$nextTick()
    this.loadLocalStorage(config)
  },
  methods: {
    disabledRange: function (date) {
      return date <= this.pastDate || date >= this.futureEnd || date > new Date()
    },
    pickDate(date) {
      let maxDate = 180

      // Get the current date and time
      let currentDate = new Date()

      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000).getTime() === currentDate.getTime()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    closePicker() {
      this.pastDate = this.momentAdd(new Date(), -6, 'M')
      this.futureEnd = moment().add(1, 'years')
    },
    async initLocalStorage() {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId: this.vgroupId ?? undefined,
        },
      })
      localStorage.setItem(
        'monthly-efficiency-config',
        JSON.stringify({
          startDate: moment(this.period[0]).format('YYYY-MM'),
          endDate: moment(this.period[1]).format('YYYY-MM'),
          vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
        })
      )
    },
    async loadLocalStorage(data) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId: this.vgroupId ?? undefined,
        },
      })
      this.period[0] = this.momentYmD(data.startDate)
      this.period[1] = this.momentYmD(data.endDate)
      this.fleetCategory = this.categoryOptions.filter((item) =>
        data.vehicleTypeId.includes(item.vehicleTypeId)
      )
      this.generateChart(this.vgroupId)
    },
    applyFilter() {
      const filter = {
        startDate: moment(this.period[0]).format('YYYY-MM'),
        endDate: moment(this.period[1]).format('YYYY-MM'),
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      }
      localStorage.setItem('monthly-efficiency-config', JSON.stringify(filter))
      this.generateChart(this.vgroupId)
    },
    async generateChart(vgroupId) {
      this.isLoading = true
      this.vgroupId = vgroupId
      const filters = {
        startDate: moment(this.period[0]).format('YYYY-MM'),
        endDate: moment(this.period[1]).format('YYYY-MM'),
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      }
      this.periodLabel[0] = moment(this.period[0]).format('MMM YYYY')
      this.periodLabel[1] = moment(this.period[1]).format('MMM YYYY')
      this.chartData.datasets = []
      const res = await this.$apollo.query({
        query: GET_MONTHLY_FUEL_COMPARISON,
        variables: {
          filter: {
            ...filters,
            column: [
              'total_distance',
              'actual_efficiency',
              'standard_price',
              'actual_price',
              'actual_total_fuel',
            ],
            orderBy: JSON.stringify({ year: 'asc' }),
            vgroupId: vgroupId ?? undefined,
            disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
          },
        },
        fetchPolicy: 'no-cache',
      })

      const data = res.data.summaryFuelMonthly.summaryFuelMonthly
      this.chartData.labels = data.map((item) => this.momentMMM(item.month))

      for (let i = 0; i < this.fleetCategory.length; i++) {
        this.chartData.datasets.push({
          type: 'line',
          label: `${data[0].actualEfficiency[i].model}/${data[0].actualEfficiency[i].type}`,
          data: data.map((item) => item.actualEfficiency[i].actualEfficiency),
          borderColor: this.colorIndex[i % this.colorIndex.length],
          fill: false,
          pointRadius: 2,
          lineTension: 0,
          borderWidth: 4,
        })
      }

      setTimeout(() => {
        this.isLoading = false
      }, 1000)
    },
  },
}
</script>
